/** Immutable AVBD augmentation constants plus explicit execution policy. */
export type AvbdProfileId =
  | 'production-robust'
  | 'official-demo3d'
  | 'paper-2025'
  | 'official-exact';

/**
 * The scheduling contract selected by a profile.  `official-exact` uses the
 * reference one-sweep, color-ordered block update available in this WebGPU
 * architecture.  Colors are ordered Gauss-Seidel, while the bounded coloring
 * overflow bucket remains a race-free Jacobi fallback; this is deliberately
 * not described as byte-for-byte serial body-order parity with demo3d.
 */
export type AvbdExecutionPolicy = Readonly<{
  iterationPolicy: 'fixed';
  adaptiveIterations: false;
  bodySolveMode: 'colored';
  schedule: 'reference-color-ordered-one-sweep';
  coloringOverflowPolicy: 'jacobi-fallback';
  serialBodyOrderParity: false;
}>;

export type AvbdParameterProfile = Readonly<{
  id: AvbdProfileId;
  label: string;
  substeps: number;
  solverIterations: number;
  linearDualUpdateBeta: number;
  angularDualUpdateBeta: number;
  regularizationAlpha: number;
  penaltyDecayGamma: number;
  pairSweeps: number;
  penaltyFloor: number;
  /** Omitted by parameter-only profiles so production scene policy is preserved. */
  executionPolicy?: AvbdExecutionPolicy;
}>;

// Values used by the authors' public avbd-demo3d implementation:
// betaLin=10000, betaAng=100, alpha=.99, gamma=.999, 10 iterations.
export const AVBD_OFFICIAL_DEMO3D_PROFILE: AvbdParameterProfile = Object.freeze({
  id: 'official-demo3d',
  label: 'Official avbd-demo3d',
  substeps: 1,
  solverIterations: 10,
  linearDualUpdateBeta: 10000.0,
  angularDualUpdateBeta: 100.0,
  regularizationAlpha: 0.99,
  penaltyDecayGamma: 0.999,
  pairSweeps: 1,
  penaltyFloor: 1.0,
});

/**
 * Default real-time profile. It keeps the official demo3d AVBD equations and
 * constants except for the slower Eq. 19 penalty decay already certified by
 * the stable production build. Official parameter parity remains available
 * explicitly through official-demo3d / official-exact.
 */
export const AVBD_PRODUCTION_ROBUST_PROFILE: AvbdParameterProfile = Object.freeze({
  ...AVBD_OFFICIAL_DEMO3D_PROFILE,
  id: 'production-robust',
  label: 'Production-robust AVBD',
  penaltyDecayGamma: 0.9999,
});

// The paper publishes beta=10, alpha=.95 and gamma=.99 in Table 2. The paper
// has one beta, so it applies to both channels here. Substeps, iterations,
// sweeps and k-start below are this WebGPU harness's explicit execution policy,
// not additional claims about fields published in Table 2.
export const AVBD_PAPER_2025_PROFILE: AvbdParameterProfile = Object.freeze({
  id: 'paper-2025',
  label: 'AVBD paper augmentation constants',
  substeps: 1,
  solverIterations: 10,
  linearDualUpdateBeta: 10.0,
  angularDualUpdateBeta: 10.0,
  regularizationAlpha: 0.95,
  penaltyDecayGamma: 0.99,
  pairSweeps: 1,
  penaltyFloor: 1.0,
});

/**
 * Opt-in fixed-iteration reference execution contract.  It intentionally
 * duplicates the public demo3d constants instead of becoming the default:
 * production-robust scenes may retain adaptive early-out and scene-certified
 * iteration counts unless this profile is explicitly selected.
 */
export const AVBD_OFFICIAL_EXACT_PROFILE: AvbdParameterProfile = Object.freeze({
  ...AVBD_OFFICIAL_DEMO3D_PROFILE,
  id: 'official-exact',
  label: 'Official avbd-demo3d fixed reference schedule',
  executionPolicy: Object.freeze({
    iterationPolicy: 'fixed',
    adaptiveIterations: false,
    bodySolveMode: 'colored',
    schedule: 'reference-color-ordered-one-sweep',
    coloringOverflowPolicy: 'jacobi-fallback',
    serialBodyOrderParity: false,
  }),
});

export const AVBD_PROFILES: Readonly<Record<AvbdProfileId, AvbdParameterProfile>> =
  Object.freeze({
    'production-robust': AVBD_PRODUCTION_ROBUST_PROFILE,
    'official-demo3d': AVBD_OFFICIAL_DEMO3D_PROFILE,
    'paper-2025': AVBD_PAPER_2025_PROFILE,
    'official-exact': AVBD_OFFICIAL_EXACT_PROFILE,
  });

export const AVBD_DEFAULT_PROFILE_ID: AvbdProfileId = 'production-robust';

// Backward-compatible reference aliases are deliberately derived from one
// profile object so app controls and source-specialized WGSL cannot drift.
export const AVBD_REFERENCE_SUBSTEPS = AVBD_OFFICIAL_DEMO3D_PROFILE.substeps;
export const AVBD_REFERENCE_SOLVER_ITERATIONS = AVBD_OFFICIAL_DEMO3D_PROFILE.solverIterations;
export const AVBD_REFERENCE_DUAL_UPDATE_BETA =
  AVBD_OFFICIAL_DEMO3D_PROFILE.linearDualUpdateBeta;
export const AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA =
  AVBD_OFFICIAL_DEMO3D_PROFILE.angularDualUpdateBeta;
export const AVBD_REFERENCE_REGULARIZATION_ALPHA =
  AVBD_OFFICIAL_DEMO3D_PROFILE.regularizationAlpha;
export const AVBD_REFERENCE_PENALTY_DECAY_GAMMA =
  AVBD_OFFICIAL_DEMO3D_PROFILE.penaltyDecayGamma;
export const AVBD_REFERENCE_PAIR_SWEEPS = AVBD_OFFICIAL_DEMO3D_PROFILE.pairSweeps;
export const AVBD_REFERENCE_PENALTY_FLOOR = AVBD_OFFICIAL_DEMO3D_PROFILE.penaltyFloor;

// Shared AVBD contact/friction tuning used by warmstart, primal, and dual stages.
export const AVBD_FRICTION_STATIC = 0.75;
// Keep dynamic equal to static for now. This matches the simpler 2D reference
// behavior and avoids static/dynamic regime switching drift ("motoring").
export const AVBD_FRICTION_DYNAMIC = 0.75;
// Keep exact parity with the public WebPhysics AVBD reference. This is a
// narrow contact-persistence tolerance, not a centimetre-wide target shell.
export const AVBD_COLLISION_MARGIN = 5e-4;
